import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { UploadCloud, CloudOff } from "lucide-react";

export default function DocUpload({ partyId, policies = [], onSaved }) {
  const { config } = useAuth();
  const [open, setOpen] = useState(false);
  const [connected, setConnected] = useState(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ category: "Policy", classification: "Internal", source: "Agency", policy_id: "", document_date: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      api.get("/dropbox/status").then(({ data }) => setConnected(data.connected)).catch(() => setConnected(false));
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      if (connected) {
        if (!file) { toast.error("Choose a file to upload."); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("party_id", partyId);
        Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
        await axios.post(`${API}/dropbox/documents/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Document uploaded to Dropbox.");
      } else {
        // metadata-only fallback
        await api.post("/documents", { party_id: partyId, filename: file ? file.name : "document.pdf", ...form, source: form.source || "Manual" });
        toast.success("Document metadata saved. Dropbox is not connected, so no file was uploaded.");
      }
      setOpen(false); onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="upload-document" className="bg-navy hover:bg-atlantic text-white">
        <UploadCloud className="h-4 w-4 mr-1.5" /> Upload Document
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">Upload Document</DialogTitle>
            <DialogDescription className="text-xs">
              {connected === false && <span className="text-seaamber flex items-center gap-1"><CloudOff className="h-3.5 w-3.5" /> Dropbox not connected — a metadata-only record will be created.</span>}
              {connected === true && <span className="text-seagreen">Dropbox connected — file will be stored in the client folder.</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-slate mb-1 block">File</Label>
              <Input type="file" data-testid="doc-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-white text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate mb-1 block">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="doc-category" className="bg-white text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-60">{(config.document_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate mb-1 block">Classification</Label>
                <Select value={form.classification} onValueChange={(v) => set("classification", v)}>
                  <SelectTrigger data-testid="doc-classification" className="bg-white text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{["Public", "Internal", "Confidential", "Restricted"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate mb-1 block">Source</Label>
                <Select value={form.source} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="doc-source" className="bg-white text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{["Client", "Carrier/MGA", "Agency", "Third Party", "System Import", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate mb-1 block">Related Policy</Label>
                <Select value={form.policy_id || "__none"} onValueChange={(v) => set("policy_id", v === "__none" ? "" : v)}>
                  <SelectTrigger data-testid="doc-policy" className="bg-white text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-60"><SelectItem value="__none">— none —</SelectItem>{policies.map((p) => <SelectItem key={p.id} value={p.id}>{p.policy_number} · {p.line}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate mb-1 block">Notes</Label>
              <Textarea data-testid="doc-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-white text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="doc-upload-submit" className="bg-navy hover:bg-atlantic text-white">{busy ? "Uploading…" : (connected ? "Upload to Dropbox" : "Save Metadata")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
